// The Inbox part: the nav item (order 10, a dot while the app's badge counts
// something), `/me/inbox` and the key `g i` (the old web's).
//
// What waits sits under the badge's key, which the notices part and the
// conversations part read again on every event that can change the badge;
// the settled items' pages are read again only when an item settles or a chat
// is renamed or deleted (queries.ts).
import { useQuery } from "@tanstack/react-query";
import { createRoute } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import type { ReactNode } from "react";
import { useApi } from "../app/context";
import { lazyScreen } from "../app/lazy";
import type { MeRouterContext } from "../app/routes";
import { defineScreenPart } from "../app/screen-part";
import { badgeKey, badgeQuery } from "../notices/queries";
import type { ShellBadge } from "../shell/outside/contract";
import { iconOf } from "../ui/icon";
import { historyChanged, inboxNowQuery } from "./queries";
import { useWorkspaceInbox, WorkspaceInboxStream } from "./workspace";

// Loaded when first opened (app/lazy.tsx). Cloud's workspace Inbox draws it too.
export const InboxScreen = lazyScreen(() => import("./screen"), "InboxScreen");

export const INBOX_PATH = "/me/inbox";

/** What the badge counts: Me's, and the workspaces' rows from elsewhere. */
function useInboxCount(): number {
  const mine = useQuery(badgeQuery(useApi())).data?.count ?? 0;
  return mine + useWorkspaceInbox().items.length;
}

/** The nav's dot as data (shell/outside). */
function useInboxBadgeData(): ShellBadge | null {
  const count = useInboxCount();
  return count > 0
    ? { kind: "dot", tone: "accent", label: m.inbox_dot() }
    : null;
}

function useInboxBadge(): ReactNode {
  const count = useInboxCount();
  return count > 0 ? (
    <span
      role="img"
      aria-label={m.inbox_dot()}
      className="inline-block h-[7px] w-[7px] rounded-full bg-accent"
    />
  ) : null;
}

export const inboxScreen = defineScreenPart({
  id: "inbox",
  // The workspaces' rows, when the host reads them from elsewhere.
  Background: WorkspaceInboxStream,
  nav: {
    order: 10,
    label: () => m.inbox_nav(),
    icon: iconOf("inbox"),
    to: INBOX_PATH,
    useBadge: useInboxBadge,
    useBadgeData: useInboxBadgeData,
  },
  routes: (main) => [
    createRoute({
      getParentRoute: () => main,
      path: "inbox",
      loader: ({ context }) => {
        const c = context as MeRouterContext;
        void c.queryClient.prefetchQuery(inboxNowQuery(c.api));
      },
      component: InboxScreen,
    }),
  ],
  // What waits (and the badge's count) is drawn at once from a kept copy.
  persist: (key) => key[0] === badgeKey[0] && key[1] === badgeKey[1],
  realtime: {
    // An open item is not among the settled ones (items never open again).
    "agent_turn.changed": (payload, { qc }) => {
      if ((payload as { state?: string } | null)?.state !== "open")
        historyChanged(qc);
    },
    // The rows show the chat's title; a deleted chat's rows go.
    "agent_chat.changed": (payload, { qc }) => {
      const change = (payload as { change?: string } | null)?.change;
      if (change === "updated" || change === "deleted") historyChanged(qc);
    },
  },
  shortcuts: [
    {
      id: "inbox.go",
      key: "g i",
      group: "navigate",
      label: () => m.inbox_go(),
      run: ({ navigate }) => navigate(INBOX_PATH),
    },
  ],
});
